import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/NotificationBell.css';

// Shared AudioContext for better browser compatibility
let sharedAudioContext = null;
let audioContextInitialized = false;

// Initialize AudioContext on first user interaction
const initAudioContext = () => {
  if (!audioContextInitialized && !sharedAudioContext) {
    try {
      sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextInitialized = true;
      // Broadcast to other tabs that audio is now unlocked
      localStorage.setItem('audioContextUnlocked', Date.now().toString());
    } catch (error) {
    }
  }
  // Resume if suspended (critical for mobile)
  if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().then(() => {
    }).catch(err => {
    });
  }
  return sharedAudioContext;
};

// Try to initialize AudioContext silently (for cross-tab support)
const tryInitAudioContextSilently = () => {
  if (!audioContextInitialized && !sharedAudioContext) {
    try {
      sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
      // Check if it's not suspended (some browsers allow this after first tab interaction)
      if (sharedAudioContext.state !== 'suspended') {
        audioContextInitialized = true;
      }
    } catch (error) {
    }
  }
};

// Create bell sound using Web Audio API
const playBellSound = () => {
  try {
    const audioContext = initAudioContext();
    if (!audioContext) {
      return;
    }
    
    // Ensure context is not suspended
    if (audioContext.state === 'suspended') {
      audioContext.resume().then(() => {
        actuallyPlayBellSound(audioContext);
      }).catch(err => {
      });
    } else {
      actuallyPlayBellSound(audioContext);
    }
  } catch (error) {
  }
};

// Actual bell sound generation
const actuallyPlayBellSound = (audioContext) => {
  try {
    // Create oscillator for bell-like sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Bell-like frequency
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.3);
    
    oscillator.type = 'sine';
    
    // Envelope for bell sound
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (error) {
  }
};

// Haptic feedback for mobile devices
const triggerHapticFeedback = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate([50, 50, 50]); // Three short pulses for notification
    return true;
  }
  return false;
};

const NotificationBell = ({ 
  notifications, 
  onDismiss, 
  onEnterRoom, 
  onClearAll, 
  doNotDisturb = false,
  onApproveSpectator = null,
  onDenySpectator = null,
  onApproveRejoin = null,
  onDenyRejoin = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [processingId, setProcessingId] = useState(null); // Track which notification is being processed
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true; // Default to enabled
  });
  const dropdownRef = useRef(null);
  const prevNotificationCountRef = useRef(notifications.length);

  // Cross-tab AudioContext initialization listener
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'audioContextUnlocked') {
        // Another tab unlocked audio - try to init our own context
        tryInitAudioContextSilently();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check on mount if audio was previously unlocked
    const lastUnlock = localStorage.getItem('audioContextUnlocked');
    if (lastUnlock) {
      tryInitAudioContextSilently();
    }
    
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Initialize AudioContext on any click/touch within the page (one-time)
  // Mobile browsers require user gesture to unlock audio
  useEffect(() => {
    const handleFirstInteraction = () => {
      initAudioContext();
      // Remove all listeners after first interaction
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('touchend', handleFirstInteraction);
    };
    
    // Listen for both click and touch events for mobile compatibility
    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction, { passive: true });
    document.addEventListener('touchend', handleFirstInteraction, { passive: true });
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('touchend', handleFirstInteraction);
    };
  }, []);

  // Filter notifications based on DND mode
  // Spectator requests always show even in DND mode
  const displayNotifications = doNotDisturb 
    ? notifications.filter(n => n.type === 'spectator_request')
    : notifications;
  
  const unreadCount = displayNotifications.filter(n => !n.read).length;

  // Play sound and haptic when new notification arrives (respects DND for non-spectator requests)
  useEffect(() => {
    if (notifications.length > prevNotificationCountRef.current) {
      // In DND mode, only alert for spectator requests
      const newNotifications = notifications.slice(prevNotificationCountRef.current);
      const hasSpectatorRequest = newNotifications.some(n => n.type === 'spectator_request');
      
      if (!doNotDisturb || hasSpectatorRequest) {
        // Trigger haptic feedback on mobile
        triggerHapticFeedback();
      }
      
      if (soundEnabled && (!doNotDisturb || hasSpectatorRequest)) {
        playBellSound();
      }
    }
    prevNotificationCountRef.current = notifications.length;
  }, [notifications.length, soundEnabled, doNotDisturb, notifications]);

  // Listen for changes from profile page
  useEffect(() => {
    const handleSoundChange = () => {
      const saved = localStorage.getItem('notificationSoundEnabled');
      setSoundEnabled(saved !== null ? JSON.parse(saved) : true);
    };
    
    window.addEventListener('notificationSoundChanged', handleSoundChange);
    return () => window.removeEventListener('notificationSoundChanged', handleSoundChange);
  }, []);

  // Save sound preference
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('notificationSoundEnabled', JSON.stringify(newValue));
      window.dispatchEvent(new Event('notificationSoundChanged'));
      return newValue;
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'room_joined':
        return '👥';
      case 'game_ready':
        return '🎮';
      case 'spectator_joined':
        return '👁️';
      case 'spectator_request':
        return '🙋';
      case 'chat_message':
        return '💬';
      case 'friend_request_accepted':
        return '🤝';
      case 'friend_request_declined':
        return '👋';
      case 'friend_request_received':
        return '📬';
      case 'rejoin_request':
        return '🔄';
      case 'rejoin_approved':
        return '✅';
      case 'rejoin_denied':
        return '❌';
      case 'trial_warning':
        return '⏳';
      case 'trial_expired':
        return '🔒';
      default:
        return '📢';
    }
  };

  // Handle bell button click/touch - also initializes AudioContext
  const handleBellClick = (e) => {
    // Prevent double firing on touch devices
    if (e.type === 'touchend') {
      e.preventDefault();
    }
    // Initialize AudioContext on user interaction (required by browsers, especially mobile)
    initAudioContext();
    setIsOpen(!isOpen);
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell-button"
        onClick={handleBellClick}
        onTouchEnd={handleBellClick}
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <span className="bell-icon">{doNotDisturb ? '🔕' : '🔔'}</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>{doNotDisturb ? 'DND Active' : 'Notifications'}</h3>
            <div className="notification-header-actions">
              <button 
                className={`sound-toggle-btn ${soundEnabled ? 'enabled' : 'disabled'}`}
                onClick={toggleSound}
                title={soundEnabled ? 'Mute notifications' : 'Enable notification sound'}
              >
                {soundEnabled ? '🔔' : '🔕'}
              </button>
              {displayNotifications.length > 0 && (
                <button 
                  className="clear-all-btn"
                  onClick={() => {
                    onClearAll();
                    setIsOpen(false);
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {doNotDisturb && (
            <div className="dnd-notice" style={{
              padding: '8px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
              fontSize: '12px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>🔕</span>
              <span>Do Not Disturb is on. Only spectator requests shown.</span>
            </div>
          )}

          <div className="notification-list">
            {displayNotifications.length === 0 ? (
              <div className="no-notifications">
                <span className="no-notif-icon">🔕</span>
                <p>{doNotDisturb ? 'No pending spectator requests' : 'No notifications'}</p>
              </div>
            ) : (
              displayNotifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                >
                  <button
                    className="dismiss-notification"
                    onClick={() => onDismiss(notification.id)}
                    aria-label="Dismiss"
                  >
                    ×
                  </button>

                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.type === 'room_joined' && '🎉 Player Joined'}
                      {notification.type === 'game_ready' && '🚀 Game Ready'}
                      {notification.type === 'spectator_joined' && '👁️ Spectator Joined'}
                      {notification.type === 'spectator_request' && '🙋 Spectator Request'}
                      {notification.type === 'chat_message' && '💬 New Message'}
                      {notification.type === 'friend_request_accepted' && '🤝 Friend Request Accepted'}
                      {notification.type === 'friend_request_declined' && '👋 Friend Request Declined'}
                      {notification.type === 'friend_request_received' && '📬 New Friend Request'}
                      {notification.type === 'rejoin_request' && '🔄 Rejoin Request'}
                      {notification.type === 'rejoin_approved' && '✅ Rejoin Approved'}
                      {notification.type === 'rejoin_denied' && '❌ Rejoin Denied'}
                      {notification.type === 'trial_warning' && '⏳ Trial Expiring Soon'}
                      {notification.type === 'trial_expired' && '🔒 Trial Ended'}
                    </div>
                    
                    <div className="notification-message">
                      <strong>{notification.roomName}</strong>
                      {notification.message && (
                        <span className="notif-detail"> • {notification.message}</span>
                      )}
                    </div>

                    <div className="notification-footer">
                      <span className="notification-time">
                        {formatTimestamp(notification.timestamp)}
                      </span>
                      
                      {/* Accept/Deny buttons for spectator requests */}
                      {notification.type === 'spectator_request' && onApproveSpectator && onDenySpectator && (
                        <div className="notification-actions">
                          <button
                            className="approve-btn"
                            disabled={processingId === notification.id}
                            onClick={async () => {
                              setProcessingId(notification.id);
                              await onApproveSpectator(notification.gameId, notification.requesterId || notification.roomName);
                              onDismiss(notification.id);
                              setProcessingId(null);
                            }}
                          >
                            {processingId === notification.id ? '...' : '✓ Allow'}
                          </button>
                          <button
                            className="deny-btn"
                            disabled={processingId === notification.id}
                            onClick={async () => {
                              setProcessingId(notification.id);
                              await onDenySpectator(notification.gameId, notification.requesterId || notification.roomName);
                              onDismiss(notification.id);
                              setProcessingId(null);
                            }}
                          >
                            {processingId === notification.id ? '...' : '✕ Deny'}
                          </button>
                        </div>
                      )}
                      
                      {/* Accept/Deny buttons for rejoin requests */}
                      {notification.type === 'rejoin_request' && onApproveRejoin && onDenyRejoin && (
                        <div className="notification-actions">
                          <button
                            className="approve-btn"
                            disabled={processingId === notification.id}
                            onClick={async () => {
                              setProcessingId(notification.id);
                              await onApproveRejoin(notification.gameId, notification.requesterId || notification.roomName);
                              onDismiss(notification.id);
                              setProcessingId(null);
                            }}
                          >
                            {processingId === notification.id ? '...' : '✓ Approve'}
                          </button>
                          <button
                            className="deny-btn"
                            disabled={processingId === notification.id}
                            onClick={async () => {
                              setProcessingId(notification.id);
                              await onDenyRejoin(notification.gameId, notification.requesterId || notification.roomName);
                              onDismiss(notification.id);
                              setProcessingId(null);
                            }}
                          >
                            {processingId === notification.id ? '...' : '✕ Deny'}
                          </button>
                        </div>
                      )}
                      
                      {/* Enter room button for other notification types */}
                      {notification.gameId && 
                       notification.type !== 'spectator_request' && 
                       notification.type !== 'rejoin_request' && (
                        <button
                          className="enter-room-btn-small"
                          onClick={() => {
                            onEnterRoom(notification.gameId, notification.roomName);
                            setIsOpen(false);
                          }}
                        >
                          {notification.type === 'chat_message' ? 'Open Chat →' : 
                           notification.type === 'rejoin_approved' ? 'Join Now →' : 'Enter Room →'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {displayNotifications.length >= 25 && (
            <div className="notification-warning">
              ⚠️ {displayNotifications.length}/30 notifications. Oldest will be auto-removed at 30.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
